import { Anchor } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Game } from 'shared/api';
import { accountRoute, gameSettingsRoute } from 'shared/routing';
import { createRegistry, RegistryRootProps } from 'shared/ui';
import { GameRegistryModel } from '../../model';
import cls from './game-registry.module.scss';

export type GameRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	tableTdActionSlot?: (game: Game) => ReactNode;
	emptyActionSlot?: ReactNode;
	filterSlot?: ReactNode;
};

export const createGameRegistry = (model: GameRegistryModel) => {
	const Registry = createRegistry(model);

	const GameRegistry = forwardRef<HTMLDivElement, GameRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				filterSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('game-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.gameRegistryTopBar}>
						{filterSlot && (
							<Registry.Filters className={cls.gameRegistryFilters}>{filterSlot}</Registry.Filters>
						)}
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='title'>
									{t('game-registry.table-columns.game')}
								</Registry.Table.ThSorter>
								{withAccount && (
									<Registry.Table.ThSorter field='account'>
										{t('game-registry.table-columns.account')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.Th>{t('game-registry.table-columns.actions')}</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({ variant: row.isLocked ? 'locked' : 'default' })}
							>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												component={Link}
												to={gameSettingsRoute}
												query={{ id: row.game.id }}
											>
												{row.game.title}
											</Anchor>
										</Registry.Table.Td>
										{withAccount && (
											<Registry.Table.Td>
												<Anchor
													component={Link}
													to={accountRoute}
													query={{ id: row.account.id }}
												>
													{row.account.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('game-registry.empty.title')}
						message={t('game-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('game-registry.table-empty.title')}
						message={t('game-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return GameRegistry;
};
